import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { bots } from '../data/bots';
import { cleanupMindStudio, getMindStudioStatus, updateMindStudioStatus, resetMindStudioStatus } from '../utils/mindstudio';
import type { Bot } from '../types';

const BotPage: React.FC = () => {
  const { botId } = useParams<{ botId: string }>();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState<string[]>([]);
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(false);
  const initTimeoutRef = useRef<NodeJS.Timeout>();
  const setupCompleteRef = useRef(false);
  const healthCheckIntervalRef = useRef<NodeJS.Timeout>();
  const initializationAttemptRef = useRef(0);

  const bot: Bot | undefined = bots.find(b => b.id === botId) || 
    bots.flatMap(b => b.subBots || []).find(sb => sb.id === botId);

  const addDebugInfo = (info: string) => {
    setDebugInfo(prev => [...prev, `${new Date().toISOString()}: ${info}`]);
    console.log('Debug:', info);
  };

  const hasInsufficientCredits = user && bot?.price ? user.credits < bot.price : false;

  const checkMindStudioHealth = () => {
    if (!mountedRef.current || !bot) return true;

    const status = getMindStudioStatus();
    const iframe = document.getElementById(`mindstudio-frame-${bot.id}`);
    const script = document.getElementById(`mindstudio-script-${bot.id}`);
    
    updateMindStudioStatus({
      iframeExists: !!iframe,
      scriptLoaded: !!script,
      lastAction: 'health_check'
    });

    if (!iframe || !script || !status.initialized) {
      addDebugInfo(`Health check failed: iframe=${!!iframe}, script=${!!script}, initialized=${status.initialized}`);
      return false;
    }

    return true;
  };

  const setupMindStudio = async () => {
    if (!mountedRef.current || !bot || setupCompleteRef.current) return;

    try {
      initializationAttemptRef.current += 1;
      addDebugInfo(`Starting bot setup (attempt ${initializationAttemptRef.current})`);

      if (initializationAttemptRef.current > 1) {
        await cleanupMindStudio();
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      resetMindStudioStatus();

      if (!bot.embedToken) {
        throw new Error('Bot configuration is missing');
      }

      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      if (hasInsufficientCredits) {
        throw new Error('Insufficient credits');
      }

      setError(null);
      setIsLoading(true);

      // Create iframe with unique instance ID
      const instanceId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const frameId = `mindstudio-frame-${bot.id}`;
      
      const iframe = document.createElement('iframe');
      iframe.id = frameId;
      iframe.style.cssText = `
        width: 100%;
        height: 100%;
        min-height: 600px;
        border: 1px solid rgba(0,0,0,0.1);
        border-radius: 8px;
        outline: none;
      `;
      iframe.className = 'w-full h-full min-h-[600px] rounded-lg';
      iframe.title = `${bot.title} Bot`;
      iframe.referrerPolicy = 'origin';
      iframe.allow = 'camera; microphone';
      iframe.setAttribute('sandbox', 'allow-scripts allow-forms allow-popups allow-downloads allow-same-origin');

      if (iframeContainerRef.current) {
        iframeContainerRef.current.innerHTML = '';
        iframeContainerRef.current.appendChild(iframe);
        updateMindStudioStatus({ iframeExists: true });
      }

      await new Promise(resolve => setTimeout(resolve, 500));
      addDebugInfo('Iframe mounted');

      // Clear any existing timeout
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }

      // Set new initialization timeout
      initTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current && !setupCompleteRef.current) {
          const status = getMindStudioStatus();
          addDebugInfo(`Initialization timeout. Status: ${JSON.stringify(status)}`);
          setError('Bot initialization timed out. Please try again.');
          setIsLoading(false);
          cleanupMindStudio();
        }
      }, 20000);

      // Initialize MindStudio
      window.MindStudioSettings = {
        publicToken: bot.embedToken,
        appId: bot.id,
        userId: user.id,
        targetId: frameId,
        debugging: true,
        options: {
          autoFocus: true,
          disableThreads: false,
          minimizeThreadPanel: false,
          launchVariables: {
            userName: user.name,
            userEmail: user.email,
            userOrganization: user.organization,
            userCredits: user.credits,
            botPrice: bot.price || 0,
            instanceId
          }
        }
      };

      // Create and load MindStudio script
      const script = document.createElement('script');
      script.id = `mindstudio-script-${bot.id}`;
      script.src = 'https://api.mindstudio.ai/v1/embed.js';
      script.async = true;
      script.crossOrigin = 'anonymous';
      
      script.onload = () => {
        addDebugInfo('MindStudio script loaded successfully');
        updateMindStudioStatus({ 
          scriptLoaded: true,
          initialized: true,
          lastAction: 'script_loaded'
        });
        setupCompleteRef.current = true;
        
        if (healthCheckIntervalRef.current) {
          clearInterval(healthCheckIntervalRef.current);
        }

        healthCheckIntervalRef.current = setInterval(checkMindStudioHealth, 10000);

        setTimeout(() => {
          if (mountedRef.current && isLoading) {
            setIsLoading(false);
          }
        }, 2000);
      };
      
      script.onerror = (error) => {
        if (mountedRef.current) {
          const errorMessage = error instanceof Error ? error.message : 'Script load failed';
          addDebugInfo(`Script load error: ${errorMessage}`);
          updateMindStudioStatus({ 
            lastError: errorMessage,
            lastAction: 'script_load_failed'
          });
          setError('Failed to load bot interface. Please try again later.');
          setIsLoading(false);
        }
      };

      // Add scripts
      document.head.appendChild(script);
      addDebugInfo('Scripts added to document');

    } catch (err) {
      if (mountedRef.current) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize bot';
        addDebugInfo(`Setup error: ${errorMessage}`);
        updateMindStudioStatus({ 
          lastError: errorMessage,
          lastAction: 'setup_failed'
        });
        setError(errorMessage);
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    setupCompleteRef.current = false;
    initializationAttemptRef.current = 0;
    addDebugInfo('Component mounted');

    if (!bot) {
      window.location.href = '/dashboard';
      return;
    }

    cleanupMindStudio().then(() => {
      setupMindStudio();
    });

    return () => {
      mountedRef.current = false;
      setupCompleteRef.current = false;
      
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
      
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
      }
      
      cleanupMindStudio();
    };
  }, [bot?.id]);

  const handleBack = () => {
    cleanupMindStudio().then(() => {
      window.location.href = '/dashboard';
    });
  };

  if (!bot) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">{bot.title}</h1>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {error ? (
            <div className="flex items-center justify-center h-[calc(100vh-200px)]">
              <div className="text-center max-w-lg px-6">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <p className="text-lg text-gray-900 font-medium mb-4">{error}</p>
                {error === 'Insufficient credits' ? (
                  <a
                    href="/credits"
                    className="inline-block px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
                  >
                    Add Credits
                  </a>
                ) : (
                  <button
                    onClick={() => {
                      setError(null);
                      setupCompleteRef.current = false;
                      setupMindStudio();
                    }}
                    className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
                  >
                    Try Again
                  </button>
                )}
                <div className="mt-4 p-4 bg-gray-100 rounded-lg text-left text-xs font-mono text-gray-600 max-h-60 overflow-y-auto">
                  <p className="font-semibold mb-2">Debug Information:</p>
                  {debugInfo.map((info, i) => (
                    <div key={i} className="mb-1">{info}</div>
                  ))}
                  <div className="mt-4 border-t pt-2">
                    <p className="font-semibold">MindStudio Status:</p>
                    <pre>{JSON.stringify(getMindStudioStatus(), null, 2)}</pre>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div 
              ref={iframeContainerRef}
              className="w-full h-[calc(100vh-200px)] min-h-[600px] flex items-center justify-center overflow-hidden"
            />
          )}
          {isLoading && !error && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-600 border-t-transparent"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BotPage;